import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { X } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { joinStrings } from "@/lib/joinString"
import { CareProviderIcon } from "@/modules/care-provider/components/care-provider-icon"
import { useCareProviderWithOrigin } from "@/modules/care-provider/services/useCareProviderWithOrigin"
import { careProviderSearchDoctorNetworksQuery$data } from "@/modules/care-provider/simple-search/__generated__/careProviderSearchDoctorNetworksQuery.graphql"
import { type ApplicableInsuranceCareProviderListIds } from "@/modules/care-provider/simple-search/services/useCareProviderQuickListQuery"
import { combineCareProviderTitle } from "@/modules/care-provider/utils/combine-care-provider-title"
import { getCareProviderIndicator } from "@/modules/care-provider/utils/get-care-provider-indicator"
import { BetterDocSuggestions } from "@/modules/patients/detail/better-doc/components/better-doc-suggestions"

type CareProviderValueProps = {
  value: string
  viewOnly?: boolean
  hasMPNReferral?: boolean
  applicableInsuranceCareProviderListIds?: ApplicableInsuranceCareProviderListIds
  doctorNetworks?: NonNullable<careProviderSearchDoctorNetworksQuery$data["ch_patientById"]>["doctorNetworks"]
  progressNoteId?: string
  onSelect?: (careProvider: GraphNode) => void
  onSetBetterDocSuggestion?: () => void
  onRemove?: () => void
}

const CareProviderValue = ({
  value,
  viewOnly,
  hasMPNReferral,
  applicableInsuranceCareProviderListIds,
  doctorNetworks,
  progressNoteId,
  onSelect,
  onSetBetterDocSuggestion,
  onRemove,
}: CareProviderValueProps) => {
  const { t } = useTranslation()
  const careProvider = useCareProviderWithOrigin(value)
  const doctorNetwork = useMemo(() => {
    return doctorNetworks?.find((x) => x.careProvider?.id === value)
  }, [doctorNetworks, value])

  if (!careProvider) {
    return (
      <div
        tabIndex={0}
        id="care-provider-value"
        className="group/healthProviderCard border-border flex h-12 w-full justify-between rounded-xl border px-4 py-1 text-sm focus:outline-hidden focus:outline-offset-0"
        data-testid="selected-care-provider">
        <span className="flex items-center">{t("careProvider.careProviderNotFound")}</span>
        <Button
          className="size-10 opacity-0 group-hover/healthProviderCard:opacity-100"
          tabIndex={-1}
          variant="circle"
          data-testid="remove-care-provider-button"
          onClick={onRemove}>
          <X />
        </Button>
      </div>
    )
  }

  const firstSpecialty = careProvider.careProviderSpecialtyTranslations?.[0]?.name
  const title = combineCareProviderTitle({
    businessName: careProvider.businessName,
    title: careProvider.title,
    firstName: careProvider.firstName,
    lastName: careProvider.lastName,
  })

  return (
    <div
      tabIndex={0}
      id="care-provider-value"
      className="group/healthProviderCard border-border bg-surface hover:bg-surface-hovered focus:outline-primary-focused flex h-12 w-full justify-between rounded-xl border px-2 py-1 focus:outline-hidden focus:outline-offset-0"
      data-testid="selected-care-provider">
      <div className="flex w-full">
        <CareProviderIcon
          groupType={careProvider.careProviderGroup.groupType}
          careProvider={careProvider}
          isHoverEnabled
          hasMPNReferral={hasMPNReferral}
          data-testid="care-provider-icon-container"
        />
        <div className="ml-2 grid grid-rows-1 truncate">
          <span data-testid="care-provider-full-name" className="truncate text-sm">
            {title}
          </span>
          <span className="text-on-surface-subdued truncate text-xs">
            {joinStrings([firstSpecialty, careProvider.groupName], ", ")}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-center group-hover/healthProviderCard:hidden">
        {getCareProviderIndicator({
          isMedgatePartnerNetworkMember: careProvider.isMedgatePartnerNetworkMember,
          isTakesNewPatients: careProvider.isTakesNewPatients,
          role: doctorNetwork?.role,
          defaultMedgatePartnerNetworkAllowed: hasMPNReferral,
          insuranceCareProviderListIds: careProvider.insuranceCareProviderListIds,
          applicableInsuranceCareProviderListIds,
          className: "grid grid-cols-[repeat(2,auto)]",
        })}
      </div>

      {!viewOnly && (
        <div className="flex opacity-0 group-hover/healthProviderCard:opacity-100">
          <BetterDocSuggestions
            selectedId={value}
            progressNoteId={progressNoteId}
            onSelectCareProvider={onSelect}
            onSetBetterDocSuggestion={onSetBetterDocSuggestion}
          />
          <Button
            className="size-10"
            tabIndex={-1}
            variant="circle"
            data-testid="remove-care-provider-button"
            onClick={onRemove}>
            <X />
          </Button>
        </div>
      )}
    </div>
  )
}

export { CareProviderValue }
